import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import GridLayout from 'react-grid-layout';

// Components
import DateRangeSelector from './components/DateRangeSelector';
import ChartWidget from './components/ChartWidget';
import ChartModal from './components/ChartModal';
import ExpandedChartView from './components/ExpandedChartView';
import DashboardDropdown from './components/DashboardDropdown';
import PlanAnimation from '../../components/Animations/PlanAnimation';

// Actions
import { getDashboardConfig, addWidget, updateWidget, deleteWidget, reorderWidgets, getBatchWidgetData, setDateRangeFilter, setGlobalFilters, resetFilters, setExpandedWidget, setActiveModal, setSelectedWidget, listDashboards } from '../../store/Dashboard';
import { getAllAgents } from '../../store/Agent';

// Styles
import './Dashboard.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { dashboardId: urlDashboardId, workspaceId } = useParams();
  const gridContainerRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(1200);
  // Store the current layout to preserve positions during drag/drop
  const [currentLayout, setCurrentLayout] = useState([]);

  // Selectors
  const { widgets, widgetData, globalFilters, expandedWidget, activeModal, selectedWidget, isLoading, loadingWidgets, dashboardList, selectedDashboardId, dashboardListLoading } = useSelector(state => state.Analytics);

  // Fetch dashboard list and agents on mount
  useEffect(() => {
    dispatch(listDashboards());
    dispatch(getAllAgents());
  }, [dispatch]);

  // Track the last selected ID to prevent double dispatch
  const lastSelectedRef = useRef(selectedDashboardId);

  // Sync URL dashboardId with Redux state (handles direct URL navigation)
  useEffect(() => {
    // Only dispatch if URL has a dashboard ID that we haven't already selected
    if (urlDashboardId && urlDashboardId !== lastSelectedRef.current) {
      lastSelectedRef.current = urlDashboardId;
      dispatch({ type: 'SELECT_DASHBOARD', payload: urlDashboardId });
    }
  }, [urlDashboardId, dispatch]);

  // Update URL when selectedDashboardId changes (from dropdown selection or initial load)
  useEffect(() => {
    if (selectedDashboardId) {
      lastSelectedRef.current = selectedDashboardId;
      // Update URL if it doesn't match
      if (urlDashboardId !== selectedDashboardId) {
        const basePath = workspaceId ? `/${workspaceId}/dashboard` : '/dashboard';
        history.replace(`${basePath}/${selectedDashboardId}`);
      }
    }
  }, [selectedDashboardId, urlDashboardId, workspaceId, history]);

  // Fetch selected dashboard config when selectedDashboardId changes
  useEffect(() => {
    if (selectedDashboardId) {
      dispatch({ type: 'GET_DASHBOARD_BY_ID', payload: { dashboardId: selectedDashboardId } });
    }
  }, [selectedDashboardId, dispatch]);

  // Fetch widget data when widgets or filters change
  useEffect(() => {
    if (widgets.length > 0) {
      dispatch(getBatchWidgetData({
        widgets: widgets.map(w => ({
          id: w.id,
          metricCategory: w.metricCategory || 'agent',
          metrics: w.metrics,
          agentId: w.agentId,
          crmObjectType: w.crmObjectType,
          crmMetrics: w.crmMetrics,
          ownerId: w.ownerId,
          pipelineId: w.pipelineId,
          filters: w.filters,  // Include advanced filters (MongoDB query)
          filterDefinition: w.filterDefinition,  // Include filter definition
          dateRange: w.dateRange,
          viewBy: w.viewBy || 'day'
        })),
        globalDateRange: globalFilters.dateRange.preset,
        globalFilters
      }));
    }
  }, [widgets, globalFilters, dispatch]);

  // Measure grid container width for responsive layout
  useEffect(() => {
    // Only measure when not loading and widgets exist
    if (isLoading || widgets.length === 0) return;

    const updateWidth = () => {
      if (gridContainerRef.current) {
        setGridWidth(gridContainerRef.current.offsetWidth);
      }
    };

    // Initial measurement after DOM is ready
    const timeoutId = setTimeout(updateWidth, 50);

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timeoutId);
    };
  }, [widgets.length, isLoading]); // Re-measure when widgets load and loading completes

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);

  // Track widget IDs and dashboard to only rebuild layout when widgets are added/removed or dashboard changes
  const prevWidgetIdsRef = useRef('');
  const prevDashboardIdRef = useRef(selectedDashboardId);
  // Track if we're in the process of adding a widget to prevent layout change callbacks from causing flicker
  const isAddingWidgetRef = useRef(false);
  const addingWidgetTimeoutRef = useRef(null);

  // Initialize layout from widgets only when widget IDs change (add/remove) or dashboard changes
  useEffect(() => {
    const currentWidgetIds = widgets.map(w => w.id).sort().join(',');
    const prevWidgetIds = prevWidgetIdsRef.current;
    const dashboardChanged = prevDashboardIdRef.current !== selectedDashboardId;

    // Reset ref if dashboard changed
    if (dashboardChanged) {
      prevDashboardIdRef.current = selectedDashboardId;
      prevWidgetIdsRef.current = '';
    }

    // Skip if same widgets (just position/order change) and dashboard hasn't changed
    if (currentWidgetIds === prevWidgetIds && currentLayout.length > 0 && !dashboardChanged) {
      return;
    }

    // Check if this is a new widget being added (not dashboard change)
    const prevIds = prevWidgetIds.split(',').filter(Boolean);
    const currentIds = currentWidgetIds.split(',').filter(Boolean);
    const isNewWidgetAdded = !dashboardChanged && prevIds.length > 0 && currentIds.length === prevIds.length + 1;

    prevWidgetIdsRef.current = currentWidgetIds;

    if (widgets.length === 0) {
      setCurrentLayout([]);
      return;
    }

    // If a new widget was added, just add it to the existing layout instead of rebuilding
    if (isNewWidgetAdded && currentLayout.length > 0) {
      const newWidgetId = currentIds.find(id => !prevIds.includes(id));
      const newWidget = widgets.find(w => w.id === newWidgetId);

      if (newWidget) {
        const w = newWidget.gridCols || (newWidget.size === 'small' ? 4 : newWidget.size === 'medium' ? 8 : 12);
        const h = newWidget.gridRows || 2;

        // Use the gridX/gridY from backend if available, otherwise calculate
        let x = newWidget.gridX ?? 0;
        let y = newWidget.gridY ?? 0;

        // If no position from backend, place at the bottom
        if (newWidget.gridX === undefined || newWidget.gridY === undefined) {
          let maxY = 0;
          currentLayout.forEach(item => {
            const bottomY = item.y + item.h;
            if (bottomY > maxY) maxY = bottomY;
          });
          y = maxY;
          x = 0;
        }

        // Set flag to prevent onLayoutChange from overwriting during grid stabilization
        isAddingWidgetRef.current = true;
        if (addingWidgetTimeoutRef.current) {
          clearTimeout(addingWidgetTimeoutRef.current);
        }
        addingWidgetTimeoutRef.current = setTimeout(() => {
          isAddingWidgetRef.current = false;
        }, 500);

        const newLayoutItem = { i: newWidget.id, x, y, w, h, minW: 2, maxW: 12, minH: 1, maxH: 4 };
        setCurrentLayout([...currentLayout, newLayoutItem]);
        return;
      }
    }

    // Check if widgets have saved positions (gridX/gridY defined)
    const hasSavedPositions = widgets.some(w => w.gridX !== undefined && w.gridY !== undefined);

    let newLayout;

    if (hasSavedPositions) {
      // Use saved positions from backend
      newLayout = widgets.map((widget) => {
        const w = widget.gridCols || (widget.size === 'small' ? 4 : widget.size === 'medium' ? 8 : 12);
        const h = widget.gridRows || 2;

        return { i: widget.id, x: widget.gridX ?? 0, y: widget.gridY ?? 0, w: w, h: h, minW: 2, maxW: 12, minH: 1, maxH: 4 };
      });
    } else {
      // Calculate positions dynamically (for new dashboards or migration)
      let x = 0;
      let y = 0;

      newLayout = widgets.map((widget) => {
        const w = widget.gridCols || (widget.size === 'small' ? 4 : widget.size === 'medium' ? 8 : 12);
        const h = widget.gridRows || 2;

        // If widget doesn't fit on current row, move to next row
        if (x + w > 12) {
          x = 0;
          y += 2;
        }

        const layoutItem = { i: widget.id, x: x, y: y, w: w, h: h, minW: 2, maxW: 12, minH: 1, maxH: 4 };
        x += w;
        return layoutItem;
      });
    }

    setCurrentLayout(newLayout);
  }, [widgets, selectedDashboardId, currentLayout.length]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addingWidgetTimeoutRef.current) {
        clearTimeout(addingWidgetTimeoutRef.current);
      }
    };
  }, []);

  // Handle layout change from react-grid-layout - just update local layout state
  const handleLayoutChange = useCallback((newLayout) => {
    // Skip layout change updates while a widget is being added to prevent flicker
    if (isAddingWidgetRef.current) {
      return;
    }
    // Always update currentLayout to preserve positions during drag/resize
    setCurrentLayout(newLayout);
  }, []);

  // Handle drag stop - save the new layout positions
  const handleDragStop = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    // Update currentLayout with the final positions
    setCurrentLayout(layout);

    // Sort layout by y then x to get visual order for widget ordering
    const sortedLayout = [...layout].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    // Reorder widgets array to match visual order
    const reorderedWidgets = sortedLayout.map(l =>
      widgets.find(w => w.id === l.i)
    ).filter(Boolean);

    dispatch({ type: 'REORDER_WIDGETS_LOCAL', payload: reorderedWidgets });
    dispatch(reorderWidgets({ widgetIds: reorderedWidgets.map(w => w.id), dashboardId: selectedDashboardId, layout: layout }));
  }, [widgets, dispatch, selectedDashboardId]);

  // Handle resize stop
  const handleResizeStop = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    // Update currentLayout with the final positions
    setCurrentLayout(layout);

    const widget = widgets.find(w => w.id === newItem.i);
    if (widget && (widget.gridCols !== newItem.w || widget.gridRows !== newItem.h || widget.gridX !== newItem.x || widget.gridY !== newItem.y)) {
      const updatedWidget = {
        ...widget,
        gridCols: newItem.w,
        gridRows: newItem.h,
        gridX: newItem.x,
        gridY: newItem.y
      };

      // Update local state
      const updatedWidgets = widgets.map(w =>
        w.id === widget.id ? updatedWidget : w
      );
      dispatch({ type: 'REORDER_WIDGETS_LOCAL', payload: updatedWidgets });

      // Save to backend
      dispatch(updateWidget({ widgetId: widget.id, widget: updatedWidget, dashboardId: selectedDashboardId }));
    }
  }, [widgets, dispatch, selectedDashboardId]);

  // Handle date range change
  const handleDateRangeChange = (dateRange) => {
    dispatch(setDateRangeFilter(dateRange));
  };

  // Handle filter reset
  const handleFilterReset = () => {
    dispatch(resetFilters());
  };

  // Handle add widget
  const handleAddWidget = (widgetConfig) => {
    dispatch(addWidget({ dashboardId: selectedDashboardId, widget: widgetConfig }));
    dispatch(setActiveModal(null));
  };

  // Handle update widget
  const handleUpdateWidget = (widgetConfig) => {
    dispatch(updateWidget({ widgetId: widgetConfig.id, widget: widgetConfig, dashboardId: selectedDashboardId }));
    dispatch(setActiveModal(null));
    dispatch(setSelectedWidget(null));
  };

  // Handle delete widget - open confirmation modal
  const handleDeleteWidget = (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    setWidgetToDelete(widget);
    setDeleteModal(true);
  };

  // Confirm delete widget
  const confirmDeleteWidget = () => {
    if (widgetToDelete) dispatch(deleteWidget({ widgetId: widgetToDelete.id, dashboardId: selectedDashboardId }));
    setDeleteModal(false);
    setWidgetToDelete(null);
  };

  // Cancel delete widget
  const cancelDeleteWidget = () => {
    setDeleteModal(false);
    setWidgetToDelete(null);
  };

  // Handle widget settings click
  const handleWidgetSettings = (widget) => {
    dispatch(setSelectedWidget(widget));
    dispatch(setActiveModal('edit'));
  };

  // Handle widget expand click
  const handleWidgetExpand = (widget) => {
    dispatch(setExpandedWidget(widget));
  };


  // Handle modal close
  const handleModalClose = () => {
    dispatch(setActiveModal(null));
    dispatch(setSelectedWidget(null));
  };

  // Handle expanded view close
  const handleExpandedClose = () => {
    dispatch(setExpandedWidget(null));
  };

  // Handle save from modal
  const handleModalSave = (widgetConfig) => {
    if (activeModal === 'edit') {
      handleUpdateWidget(widgetConfig);
    } else {
      handleAddWidget(widgetConfig);
    }
  };

  const rowHeight = 140; // Height per row unit

  return (
    <div className="configurable-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-left">
        </div>
        <div className="header-right">
          <DashboardDropdown />
          <DateRangeSelector value={globalFilters.dateRange} onChange={handleDateRangeChange} />
          <button className="reset-btn" onClick={handleFilterReset}>
            Reset
          </button>
          <button className="btn-style-new" onClick={() => dispatch(setActiveModal('add'))} >
            <i className="icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </i>
            Add
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="dashboard-loading">
          <PlanAnimation />
        </div>
      )}

      {/* Widgets Grid */}
      {!isLoading && widgets.length > 0 && (
        <div ref={gridContainerRef} className="widgets-grid-container">
          <GridLayout
            key={selectedDashboardId}
            className="widgets-grid"
            layout={currentLayout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={true}
            isResizable={true}
            draggableHandle=".drag-handle"
            resizeHandles={['se']}
            onLayoutChange={handleLayoutChange}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            useCSSTransforms={true}
            compactType="vertical"
            preventCollision={false}
          >
            {widgets.map((widget, index) => {
              const gridCols = widget.gridCols || (widget.size === 'small' ? 4 : widget.size === 'medium' ? 8 : 12);
              const gridRows = widget.gridRows || 2;

              return (
                <div key={widget.id} className="widget-wrapper">
                  <ChartWidget
                    widget={{ ...widget, gridCols, gridRows }}
                    data={widgetData[widget.id]}
                    isLoading={loadingWidgets[widget.id]}
                    onSettings={handleWidgetSettings}
                    onExpand={handleWidgetExpand}
                    onDelete={handleDeleteWidget}
                    widgetIndex={index}
                  />
                </div>
              );
            })}
          </GridLayout>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && widgets.length === 0 && (
        <div className="dashboard-empty">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
          </div>
          <h3>No widgets yet</h3>
          <p>Add your first widget to start building your analytics dashboard</p>
          <button
            className="btn-style-new"
            onClick={() => dispatch(setActiveModal('add'))}
          >
            + Add Widget
          </button>
        </div>
      )}

      {/* Chart Modal (Add/Edit) */}
      <ChartModal
        isOpen={activeModal === 'add' || activeModal === 'edit'}
        onClose={handleModalClose}
        mode={activeModal}
        widgetData={selectedWidget}
        onSave={handleModalSave}
      />

      {/* Expanded Chart View */}
      <ExpandedChartView
        isOpen={!!expandedWidget}
        onClose={handleExpandedClose}
        widget={expandedWidget}
        data={expandedWidget ? widgetData[expandedWidget.id] : null}
        isLoading={expandedWidget ? loadingWidgets[expandedWidget.id] : false}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} className="main-modal modal-dialog-centered new-user-modal bulkedit-modal">
        <ModalHeader toggle={cancelDeleteWidget}></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fillOpacity='0.15' />
                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                </i>
                Delete Widget
              </h4>
              <p>Are you sure you want to delete <b>{widgetToDelete?.title}</b>? You can't undo this action afterwards.</p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={cancelDeleteWidget}>Cancel</button>
                  <button className='btn-style-new' onClick={confirmDeleteWidget}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Dashboard;
